import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Youtube, Instagram, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import "./ShortsFeedPage.css";

export default function ShortsFeedPage() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [autoScrollSec, setAutoScrollSec] = useState(0);
  const scrollerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const autoTimerRef = useRef(null);

  // Initial load + settings
  useEffect(() => {
    const init = async () => {
      try {
        const s = await api.get("/settings/public");
        setAutoScrollSec(Number(s.data?.shorts_autoscroll_seconds) || 0);
      } catch (_) { /* noop */ }
      await loadMore(null, true);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(async (cur, isFirst = false) => {
    if (loading || done) return;
    setLoading(true);
    try {
      const r = await api.get("/shorts-feed", { params: { limit: 10, cursor: cur ?? undefined } });
      const newItems = r.data?.items || [];
      setItems((prev) => isFirst ? newItems : [...prev, ...newItems]);
      const next = r.data?.next_cursor;
      setCursor(next);
      if (next === null || next === undefined) setDone(true);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [loading, done]);

  // Infinite scroll via IntersectionObserver on the loadMoreRef sentinel
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !done) {
        loadMore(cursor);
      }
    }, { root: scrollerRef.current, threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [cursor, loading, done, loadMore]);

  // Auto-scroll
  useEffect(() => {
    if (!autoScrollSec || autoScrollSec <= 0) return;
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      // Scroll by one card height (CSS makes each card 100% viewport height)
      scroller.scrollBy({ top: scroller.clientHeight, behavior: "smooth" });
    }, autoScrollSec * 1000);
    return () => { if (autoTimerRef.current) clearInterval(autoTimerRef.current); };
  }, [autoScrollSec, items.length]);

  const Icon = ({ p }) => p === "youtube" ? <Youtube size={14} /> : p === "instagram" ? <Instagram size={14} /> : <PlayCircle size={14} />;

  return (
    <div className="shorts-feed-root" data-testid="shorts-feed-page">
      <Link to="/" className="shorts-feed-back" data-testid="shorts-feed-back">
        <ArrowLeft size={18} /> Volver
      </Link>
      <div className="shorts-feed-scroller" ref={scrollerRef}>
        {items.map((it) => (
          <div key={it.id} className="shorts-feed-card" data-testid={`short-card-${it.id}`}>
            <div className="shorts-feed-player">
              <iframe
                title={it.title || it.video_id}
                src={it.embed_url}
                className="shorts-feed-iframe"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="shorts-feed-info">
              <div className="shorts-feed-badge"><Icon p={it.provider} /> <span>{it.provider}</span></div>
              {it.title && <p className="shorts-feed-title">{it.title}</p>}
              <a href={it.url} target="_blank" rel="noreferrer" className="shorts-feed-link">
                <ExternalLink size={12} /> Ver en {it.provider}
              </a>
            </div>
          </div>
        ))}
        <div ref={loadMoreRef} className="shorts-feed-sentinel">
          {loading && <span>Cargando…</span>}
          {done && items.length === 0 && <span>No hay shorts todavía.</span>}
          {done && items.length > 0 && <span>Fin del feed</span>}
        </div>
      </div>
    </div>
  );
}
